import React, { useState } from 'react';
import { XIcon, EnvelopeIcon, ArrowRightIcon, CircleNotchIcon, LockIcon } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
    const { loginWithGoogle, loginWithEmail, loginWithPassword, signupWithPassword } = useAuth();
    const [mode, setMode] = useState<'magic' | 'password' | 'signup'>('magic');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fullName, setFullName] = useState('');
    const [username, setUsername] = useState('');

    if (!isOpen) return null;

    const handleGoogleLogin = async () => {
        setError(null);
        try {
            await loginWithGoogle();
            // Google redirect happens automatically
        } catch (err: any) {
            setError(err.message || 'Failed to initialize Google login');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;

        setIsSubmitting(true);
        setError(null);

        try {
            if (mode === 'magic') {
                await loginWithEmail(email);
                setSent(true);
            } else if (mode === 'password') {
                await loginWithPassword(email, password);
                onClose();
            } else if (mode === 'signup') {
                // Username will be auto-generated if not provided
                await signupWithPassword(email, password, fullName || undefined, username || undefined);
                // If sign up succeeds (and we are here), it likely means we need to confirm email
                setSent(true);
            }
        } catch (err: any) {
            console.error(err);
            // Better error message for common Duplicate User error
            if (err.message && err.message.includes("User already registered")) {
                setError("This email is already registered. Try logging in instead.");
            } else {
                setError(err.message || 'Authentication failed. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const getButtonLabel = () => {
        if (mode === 'magic') return 'Send Login Link';
        if (mode === 'password') return 'Log In';
        if (mode === 'signup') return 'Create Account';
        return 'Submit';
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-zinc-900/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative w-full max-w-md bg-zinc-950 border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                >
                    <XIcon className="w-5 h-5" />
                </button>

                <div className="p-8">
                    <div className="mb-8 text-center">
                        <h2 className="text-2xl font-heading font-bold text-white mb-2">Welcome Back</h2>
                        <p className="text-zinc-400 text-sm">Sign in to your creative workspace</p>
                    </div>

                    {error && (
                        <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-xs text-center">
                            {error}
                        </div>
                    )}

                    {sent ? (
                        <div className="text-center py-8 animate-in fade-in slide-in-from-bottom-4">
                            <div className="w-16 h-16 bg-green-500/10 text-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                                <EnvelopeIcon className="w-8 h-8" />
                            </div>
                            <h3 className="text-white font-bold mb-2">Check your inbox!</h3>
                            <p className="text-zinc-400 text-sm mb-6">
                                We sent a magic link to <span className="text-white font-medium">{email}</span>
                            </p>
                            <button
                                onClick={onClose}
                                className="text-zinc-500 hover:text-white text-xs underline"
                            >
                                Close this window
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* Google Login */}
                            <button
                                onClick={handleGoogleLogin}
                                className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white text-black font-bold rounded-xl hover:bg-zinc-200 transition-colors"
                            >
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                </svg>
                                Continue with Google
                            </button>

                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-white/10" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-zinc-950 px-2 text-zinc-500 font-medium tracking-wider">or email</span>
                                </div>
                            </div>

                            {/* Email/Password Form */}
                            <form onSubmit={handleSubmit} className="space-y-3">
                                <div className="space-y-3">
                                    {/* Signup Extra Fields */}
                                    {mode === 'signup' && (
                                        <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Full Name (optional)"
                                                    value={fullName}
                                                    onChange={(e) => setFullName(e.target.value)}
                                                    className="w-full bg-zinc-900 border border-white/10 rounded-xl py-3 pl-4 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 transition-all text-sm"
                                                />
                                            </div>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Username (optional - auto-generated)"
                                                    value={username}
                                                    onChange={(e) => setUsername(e.target.value)}
                                                    className="w-full bg-zinc-900 border border-white/10 rounded-xl py-3 pl-4 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 transition-all text-sm"
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="relative">
                                        <EnvelopeIcon className="absolute left-3 top-3.5 w-4 h-4 text-zinc-500" />
                                        <input
                                            type="email"
                                            placeholder="name@example.com"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full bg-zinc-900 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 transition-all text-sm"
                                            required
                                        />
                                    </div>

                                    {(mode === 'password' || mode === 'signup') && (
                                        <div className="relative animate-in fade-in slide-in-from-top-2">
                                            <LockIcon className="absolute left-3 top-3.5 w-4 h-4 text-zinc-500" />
                                            <input
                                                type="password"
                                                placeholder="Password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className="w-full bg-zinc-900 border border-white/10 rounded-xl py-3 pl-10 pr-4 text-white placeholder:text-zinc-600 focus:outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/50 transition-all text-sm"
                                                required
                                                minLength={6}
                                            />
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full flex items-center justify-center gap-2 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? <CircleNotchIcon className="w-4 h-4 animate-spin" /> : getButtonLabel()}
                                    {!isSubmitting && <ArrowRightIcon className="w-4 h-4" />}
                                </button>
                            </form>

                            {/* Mode Toggles */}
                            <div className="flex flex-col gap-2 pt-2 text-center">
                                {mode === 'magic' ? (
                                    <button
                                        type="button"
                                        onClick={() => setMode('password')}
                                        className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                                    >
                                        Use password instead
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setMode('magic')}
                                        className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                                    >
                                        Back to magic link
                                    </button>
                                )}

                                {mode === 'password' && (
                                    <div className="text-xs text-zinc-600">
                                        Don't have an account? {' '}
                                        <button onClick={() => setMode('signup')} className="text-zinc-400 hover:text-white transition-colors">Sign up</button>
                                    </div>
                                )}

                                {mode === 'signup' && (
                                    <div className="text-xs text-zinc-600">
                                        Already have an account? {' '}
                                        <button onClick={() => setMode('password')} className="text-zinc-400 hover:text-white transition-colors">Log in</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
